import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import jsYaml from "js-yaml";
import { AgeFilterFeature, getAgeFilter, getDateFilter } from "@emisgroup/cohort-builder";
import { DateRange } from "@emisgroup/query-builder-library/build/dist/components/dateRangePicker/dateRangePickerTypes";
import { useTranslation } from "@emisgroup/acp-application-intl";
import { Environments, EthnicityType } from "@emisgroup/cohort-builder-kit";
import { IRootState } from "../../../../../reducer/CombineReducer";
import { ResetBandingTabEntries, saveBandDetails } from "../Script";
import { BandModeType, BandsTypes } from "../Types";
import { BandHeaderText, RANGE_FROM, RANGE_TO } from "../Constant";
import { DateFeature } from "../BandingAgeForm/MockData";
import { ListType, ListTypeForYaml } from "../BandingEthnicityForm/Types";
import { AgeFeature } from "../BandingAgeForm/Data";
import { AgeTypes } from "../BandingAgeForm/Types";
import { useUser } from "../../../../../AuthContext";
import { OutputBandingPopupView } from "./OutputBandingPopup.view";

/**
 * Responsible for rendering banding popup
 * @returns Banding popup ui
 */
export const OutputBandingPopup: React.FC = () => {
  const dispatch = useDispatch();
  const { t: translate } = useTranslation();
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [bandingName, setBandingName] = useState("");
  const outputConfig = useSelector((state: IRootState) => state.outputConfig);
  const currentValues = outputConfig.selectedGenders;
  const selectedConfigId = outputConfig.selectedOutputConfig.id;
  const selectedBandingDefinition = outputConfig.selectedGenders;
  const { bandMode, name, bandType, ethnicityCategory, bandGuid } = outputConfig.selectedBandingDetails;
  const [ethnicFilter, setEthnicFilter] = useState<string | undefined>(ethnicityCategory);
  const ethinicityEntryData = outputConfig.entryData;
  const header = bandType && BandHeaderText[bandType] || "";
  const isValidEthnicityEntry = (Boolean(ethnicFilter) && ethinicityEntryData.data.lists.length > 0);
  const { selectedFromType, selectedToType } = outputConfig;
  const ageFrom = outputConfig.selectedAgeValueFrom.data;
  const ageTo = outputConfig.selectedAgeValueTo.data;
  const dateFrom = outputConfig.selectedAgeRangeFrom.value;
  const dateTo = outputConfig.selectedAgeRangeTo.value;
  const env = process.env.APP_TARGET_ENV as Environments;
  const { user } = useUser();

  /**
   * To get the age band validation
   * @returns the boolean falg
   */
  const getAgeValidations = (): boolean => {
    /**
     * To get the from validation
     * @returns boolean
     */
    const isValidFromSelection = (): boolean => {
      let flag = true;
      if (selectedFromType === AgeTypes.AGE) {
        flag = Boolean((ageFrom?.operator?.value && ageFrom?.value));
      } else if (selectedFromType === AgeTypes.NONE) {
        flag = false;
      }
      return flag;
    };

    /**
     * To get the to validation
     * @returns boolean
     */
    const isValidToSelection = (): boolean => {
      let flag = true;
      if (selectedToType === AgeTypes.AGE) {
        flag = Boolean((ageTo?.operator?.value && ageTo?.value));
      } else if (selectedToType === AgeTypes.NONE) {
        flag = false;
      }
      return flag;
    };

    const isAgeFromSelected = selectedFromType === AgeTypes.AGE;
    const isAgeToSelected = selectedToType === AgeTypes.AGE;

    const isFromValid = isAgeFromSelected ?
      Boolean((ageFrom?.operator?.value && ageFrom?.value)) :
      isValidFromSelection();
    const isToValid = isAgeToSelected ?
      Boolean((ageTo?.operator?.value && ageTo?.value)) :
      isValidToSelection();

    const isAgeFromValid = isAgeFromSelected ? isFromValid : (isFromValid || isToValid);
    const isAgeToValid = isAgeToSelected ? isToValid : (isFromValid || isToValid);
    const isValidAgeBand = (isAgeFromSelected && isAgeToSelected) ?
      (isFromValid && isToValid) :
      (isAgeFromValid && isAgeToValid);

    return isValidAgeBand;
  };

  const isValidAgeBand = getAgeValidations();

  // Renders when there is selected band id is available
  useEffect(() => {
    setShowDialog(
      bandMode === BandModeType.EDIT ||
      bandMode === BandModeType.ADD
    );
  }, [bandMode]);

  /**
   * Method for closing the form dialog
   */
  const onClosePopup = (): void => {
    setShowDialog(false);
    ResetBandingTabEntries(dispatch);
  };

  useEffect(() => {
    setBandingName("");
  }, [bandType]);

  const isValidName = Boolean(bandingName);

  /**
   * Method for handing bandname
   * @param name Bandname
   */
  const handleBandNameChange = (name: string): void => {
    setBandingName(name);
  };

  /**
   * Handler for filter change
   * @param selectedVal selected filter
   */
  const handleFilterChange = (selectedVal: EthnicityType): void => {
    setEthnicFilter(selectedVal);
  };

  useEffect(() => {
    if(name)
      setBandingName(name);
  }, [name]);

  /**
   * To return the age defintion
   * @returns the age definition
   */
  const getAgeDefinition = (): string[] => {
    const definition: string[] = [];
    if(selectedFromType === AgeTypes.DATE) {
      const newDateFrom = dateFrom as DateRange;
      const dateFromClint = getDateFilter(newDateFrom, DateFeature.featureDetail);
      definition.push(dateFromClint[0]);
    } else if(selectedFromType === AgeTypes.AGE) {
      const ageFromFeature = { ...AgeFeature, ...ageFrom } as unknown as AgeFilterFeature;
      const ageFromClint = getAgeFilter(ageFromFeature) ;
      definition.push(ageFromClint[0]);
    }

    if (selectedToType === AgeTypes.DATE) {
      const newDateTo = dateTo as DateRange;
      const dateToClint = getDateFilter(newDateTo, DateFeature.featureDetail);
      definition.push(dateToClint[0]);
    } else if(selectedToType === AgeTypes.AGE) {
      const ageToFeature = { ...AgeFeature, ...ageTo } as unknown as AgeFilterFeature;
      const ageToClint = getAgeFilter(ageToFeature);
      definition.push(ageToClint[0]);
    }

    return definition;
  };

  /**
   * To get the selected range filters
   * @returns either range from or range to
   */
  const getSelectedRangeFilters = (): string => {
    if (selectedFromType === AgeTypes.NONE) {
      return RANGE_TO;
    }
    if (selectedToType === AgeTypes.NONE) {
      return RANGE_FROM;
    }
    return "";
  };

  /**
   * To get the definition for the band
   * @param type Band type
   * @returns the definition
   */
  const getDefinition = (type: BandsTypes): string => {
    let definition = "";
    let ageDefinitionObject;
    if (type === BandsTypes.AGE) {
      const ageDefinition = getAgeDefinition();
      if(selectedFromType === AgeTypes.NONE || selectedToType === AgeTypes.NONE) {
        ageDefinitionObject = {
          design: [getSelectedRangeFilters()],
          run: ageDefinition
        };
      } else {
        ageDefinitionObject = {
          run: ageDefinition
        };
      }
      // To convert as YAML
      const formattedAgeDefinition = jsYaml.dump(ageDefinitionObject);
      definition = formattedAgeDefinition;
    } else if (type === BandsTypes.GENDER) {
      const genderList = selectedBandingDefinition.join(":");
      const prefix = "filter:property:Sex:";
      const genderDefinitionObject = {
        run: [`${prefix}${genderList}`]
      };
      // To convert as YAML
      const formattedGenderDefinition = jsYaml.dump(genderDefinitionObject);
      definition = formattedGenderDefinition;
    } else if (type === BandsTypes.ETHNICITY) {
      const runParam  = ethinicityEntryData?.data?.lists.map((list: ListType) => list.documentText).join(":");

      /**
       * To construct the yaml for the ethnicity
       * @param name ethnicity name
       * @param id ethnicity code list id
       * @returns yaml
       */
      const createResourceObject = (name: string, id: string): ListTypeForYaml => {
        return {
          ["resource-id"]: `${id}`,
          name: `${name}`,
          type: "ethnicity"
        };
      };

      const resources = ethinicityEntryData?.data?.lists?.map((item: ListType) => {
        return createResourceObject(item.documentText, item.id);
      });

      const ethnicityList = {
        run: [`reference:${runParam}`],
        resources: resources
      };

      // To convert as YAML
      const formattedDefinition = jsYaml.dump(ethnicityList);
      definition = formattedDefinition;
    }

    return definition;
  };

  /**
   * Method for handling the save for age,gender and ethinicty band
   */
  const handleSave = (): void => {
    setShowDialog(false);
    saveBandDetails(
      dispatch,
      translate,
      selectedConfigId,
      bandType as BandsTypes,
      bandingName || name,
      getDefinition(bandType as BandsTypes),
      ethnicFilter as EthnicityType,
      bandGuid,
      env,
      user?.token
    );
  };

  return (
    <OutputBandingPopupView
      onClosePopup={onClosePopup}
      showDialog={showDialog}
      value={currentValues}
      handleBandNameChange={handleBandNameChange}
      isValidName={isValidName}
      handleSave={handleSave}
      headerText={header}
      selectedBandingType={bandType as BandsTypes}
      onFilterChange={handleFilterChange}
      isValidEthnic={isValidEthnicityEntry}
      isValidAgeBand={isValidAgeBand}
      bandingName={bandingName}
    />
  );
};
